import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ConfigService } from '../config/config.service';
import { HttpErrorService } from '../http-error.service';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { CombinedUsage, CustomerManagementStats, ExportData, ExportRetryData, ForecastedUsageApiResponse, FormattedUsage, PlatformMetricsResult, TenantMetricBreakdown, UsageApiResponse } from 'src/app/app.types';
import { catchError, map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class UsageAmountsService {
  private currentUsage$ = new BehaviorSubject<CombinedUsage>(null);
  currentUsage = this.currentUsage$.asObservable();

  /**
   * CTOR
   * @param {HttpClient} http
   * @param {ConfigService} configService
   * @param {HttpErrorService} httpErrorService
   */
  constructor(
    private http: HttpClient,
    private configService: ConfigService,
    private httpErrorService: HttpErrorService
  ) {}

  getMonthlyAmounts(): Observable<CustomerManagementStats[]> {
    const url = `${this.configService.CEL_API_URI}/Usage/customermanagement`;
    
    return this.http.get<CustomerManagementStats[]>(url).pipe(
          map((res) => {
            return res;
          }),
          catchError((err) => {
            this.httpErrorService.handleHttpError(err);
            return of(err);
          })
        );
  }

  getFixedAmounts(customerRequests: Record<string, Date>)
    : Observable<CustomerManagementStats[]> 
  {
    const url = `${this.configService.CEL_API_URI}/usermanagement/Users/customermanagementstats`;

    return this.http.post<CustomerManagementStats[]>(url, {tenancySubDateMap: customerRequests}).pipe(
      map((res) => {
        return res;
      }),
      catchError((err) => {
        this.httpErrorService.handleHttpError(err);
        return of(err);
      })
    );
  }

  getEvidenceLockerStatsSingle(tenantId: string, startDate: string)
    : Observable<CustomerManagementStats> 
  {
    const url = `${this.configService.CEL_API_URI}/usermanagement/Users/internalcustomermanagementstatssingle`;

    return this.http.post<CustomerManagementStats>(url, {tenantId, startDate}).pipe(
      map((res) => {
        return res;
      }),
      catchError((err) => {
        this.httpErrorService.handleHttpError(err);
        return of(err);
      })
    );
  }

  getActiveUsersCount(tenantId: string, startDate: string, endDate: string)
    : Observable<number> 
  {
    const url = `${this.configService.CEL_API_URI}/usermanagement/Users/internalactiveusercount?tenantId=${tenantId}&startDate=${startDate}&endDate=${endDate}`;

    return this.http.get<number>(url).pipe(
      map((res) => {
        return res;
      }),
      catchError((err) => {
        this.httpErrorService.handleHttpError(err);
        return of(err);
      })
    );
  }

  getDetailedUsage(tenantId: string, startDate: string, endDate: string)
    : Observable<UsageApiResponse> 
  {
    const url = `${this.configService.CEL_API_URI}/Usage/customermanagementdetailedusage?tenantId=${tenantId}&startDate=${startDate}&endDate=${endDate}`;

    return this.http.get<UsageApiResponse>(url).pipe(
      map((res) => {
        return res;
      }),
      catchError((err) => {
        this.httpErrorService.handleHttpError(err);
        return of(err);
      })
    );
  }

  getForecast(tenantId: string, startDate: string, months: number, endOfFirstRetention: Date | null)
    : Observable<ForecastedUsageApiResponse> 
  {
    const endOfFirstRetentionParam = !endOfFirstRetention ? null : endOfFirstRetention.toISOString();
    const url = `${this.configService.CEL_API_URI}/Usage/customermanagementforecast?tenantId=${tenantId}&startDate=${startDate}&months=${months}&endOfFirstRetention=${endOfFirstRetentionParam}`;

    return this.http.get<ForecastedUsageApiResponse>(url).pipe(
      map((res) => {
        return res;
      }),
      catchError((err) => {
        this.httpErrorService.handleHttpError(err);
        return of(err);
      })
    );
  }

  getExportStats(tenantId: string, startDate: string, endDate: string)
    : Observable<ExportData> 
  {
    const url = `${this.configService.CEL_API_URI}/VideoExportsInternal/videoexportstats?TenantId=${tenantId}&StartDate=${startDate}&EndDate=${endDate}`;

    return this.http.get<ExportData>(url).pipe(
      map((res) => {
        return res;
      }),
      catchError((err) => {
        this.httpErrorService.handleHttpError(err);
        return of(err);
      })
    );
  }

  getRetryExportStats(tenantId: string, startDate: string, endDate: string)
    : Observable<ExportRetryData> 
  {
    const url = `${this.configService.CEL_API_URI}/VideoExportsInternal/videoexportretrystats?TenantId=${tenantId}&StartDate=${startDate}&EndDate=${endDate}`;

    return this.http.get<ExportRetryData>(url).pipe(
      map((res) => {
        return res;
      }),
      catchError((err) => {
        this.httpErrorService.handleHttpError(err);
        return of(err);
      })
    );
  }
  
  getPlatformMetrics(months: number, currentOnly: boolean): Observable<PlatformMetricsResult>
  {
    const url = `${this.configService.CEL_API_URI}/Usage/platformmetrics?months=${months}&currentOnly=${currentOnly}`;

    return this.http.get<PlatformMetricsResult>(url).pipe(
      map((res) => {
        return res;
      }),
      catchError((err) => {
        this.httpErrorService.handleHttpError(err);
        return of(err);
      })
    );
  }

  getTenantBreakdown(months: number, currentOnly: boolean): Observable<TenantMetricBreakdown[]>
  {
    const url = `${this.configService.CEL_API_URI}/Usage/platformmetrics/tenants?months=${months}&currentOnly=${currentOnly}`;

    return this.http.get<TenantMetricBreakdown[]>(url).pipe(
      map((res) => {
        return res;
      }),
      catchError((err) => {
        this.httpErrorService.handleHttpError(err);
        return of(err);
      })
    );
  }

    changeCurrentUsage(usage: CombinedUsage){
      this.currentUsage$.next(usage);
    }
}
